import { ROUTE_PATHS } from "../../../routes";
import { iPageCrumb } from "../../../context/PageCrumbContext";
import { iSideNavItem } from "./SideNav";

export interface iHeaderCrumb {
	label: string;
	href?: string;
}

/** Routes that show a nav list under another path. */
const NAV_ALIASES: Record<string, string> = {
	[ROUTE_PATHS.DASHBOARD]: ROUTE_PATHS.AI_DIRECT_REQUEST,
	[ROUTE_PATHS.ACTIVE_EVENTS]: ROUTE_PATHS.DIRECTORY_REQUEST_EVENTS,
};

/** The path the nav treats the current page as. */
export const navPathname = (pathname: string) => NAV_ALIASES[pathname] ?? pathname;

/** The nav entry (item or child key) with this href. */
export const navKeyForHref = (items: iSideNavItem[], href?: string) =>
	(href &&
		items.flatMap((item) => [item, ...(item.children ?? [])]).find((entry) => entry.href === href)?.key) ||
	null;

/** Section › page for a nav entry — the section links to its first list. */
const navCrumbs = (items: iSideNavItem[], key: string | null): iHeaderCrumb[] => {
	const group = items.find((item) => item.children?.some((child) => child.key === key));
	const child = group?.children?.find((entry) => entry.key === key);
	if (group && child) {
		return [
			{ label: group.label, href: group.children?.[0]?.href },
			{ label: child.label, href: child.href },
		];
	}
	const item = items.find((entry) => entry.key === key);
	return item ? [{ label: item.label, href: item.href }] : [];
};

/**
 * The header breadcrumb, shown only on detail pages: section › list › item. A detail page names itself and its
 * list (`usePageCrumb`); other pages already carry their title, so they get no breadcrumb.
 */
export const headerTrail = (
	items: iSideNavItem[],
	activeKey: string | null,
	pageCrumb: iPageCrumb | null,
): iHeaderCrumb[] => {
	if (!pageCrumb) return [];
	const parentKey = navKeyForHref(items, pageCrumb.parentHref);
	const parent = parentKey
		? navCrumbs(items, parentKey)
		: pageCrumb.parentLabel
			? [{ label: pageCrumb.parentLabel, href: pageCrumb.parentHref }]
			: navCrumbs(items, activeKey);
	return [...parent, { label: pageCrumb.label }];
};
